import { useQuery, useQueryClient, type UseQueryResult } from '@tanstack/react-query';
import { useCallback } from 'react';
import type {
  CustomerReport,
  Dashboard,
  FinancialReport,
  PnlBasis,
  ProductionReport,
  SalesReport,
  StaffProductivityReport,
} from '@tailoring-crm/shared';
import { useApiClient } from '../api/api-context.js';
import {
  getCustomerReport,
  getDashboard,
  getFinancialReport,
  getProductionReport,
  getSalesReport,
  getStaffReport,
  type RangeParams,
} from './reports-api.js';

/**
 * The reporting data layer.
 *
 * Nothing here mutates anything, so there are no mutations — only queries, keyed by the exact
 * parameters they were asked with. That keying matters more than usual on this screen: an owner
 * flicks between "this month" and "last month" and back, and a key that ignored the range would
 * show them last month's figures under this month's heading for as long as the refetch took.
 *
 * The dashboard is given a short `staleTime` rather than none. The server already caches it for
 * a minute, so refetching harder than that only spends the shop's data allowance to receive the
 * same snapshot again.
 */

export const reportKeys = {
  all: ['reports'] as const,
  dashboard: () => ['reports', 'dashboard'] as const,
  sales: (params: RangeParams & { limit?: number }) => ['reports', 'sales', params] as const,
  production: (params: RangeParams) => ['reports', 'production', params] as const,
  staff: (params: RangeParams) => ['reports', 'staff', params] as const,
  customers: (params: RangeParams & { lapsedDays?: number }) =>
    ['reports', 'customers', params] as const,
  financial: (params: RangeParams & { basis: PnlBasis }) =>
    ['reports', 'financial', params] as const,
};

const DASHBOARD_STALE_MS = 60_000;

export function useDashboard(options: { enabled?: boolean } = {}): UseQueryResult<Dashboard> {
  const client = useApiClient();
  return useQuery({
    queryKey: reportKeys.dashboard(),
    queryFn: () => getDashboard(client),
    enabled: options.enabled ?? true,
    staleTime: DASHBOARD_STALE_MS,
  });
}

/**
 * The refresh button.
 *
 * It asks the server to skip its own cache and then writes the answer straight into the query
 * cache, rather than invalidating and letting a refetch ask again without `refresh` — which
 * would return the snapshot this call just replaced and make the button look broken.
 */
export function useRefreshDashboard(): () => Promise<void> {
  const client = useApiClient();
  const queryClient = useQueryClient();
  return useCallback(async () => {
    const fresh = await getDashboard(client, { refresh: true });
    queryClient.setQueryData(reportKeys.dashboard(), fresh);
  }, [client, queryClient]);
}

export function useSalesReport(
  params: RangeParams & { limit?: number },
  options: { enabled?: boolean } = {},
): UseQueryResult<SalesReport> {
  const client = useApiClient();
  return useQuery({
    queryKey: reportKeys.sales(params),
    queryFn: () => getSalesReport(client, params),
    enabled: options.enabled ?? true,
  });
}

export function useProductionReport(
  params: RangeParams,
  options: { enabled?: boolean } = {},
): UseQueryResult<ProductionReport> {
  const client = useApiClient();
  return useQuery({
    queryKey: reportKeys.production(params),
    queryFn: () => getProductionReport(client, params),
    enabled: options.enabled ?? true,
  });
}

export function useStaffReport(
  params: RangeParams,
  options: { enabled?: boolean } = {},
): UseQueryResult<StaffProductivityReport> {
  const client = useApiClient();
  return useQuery({
    queryKey: reportKeys.staff(params),
    queryFn: () => getStaffReport(client, params),
    enabled: options.enabled ?? true,
  });
}

export function useCustomerReport(
  params: RangeParams & { limit?: number; lapsedDays?: number },
  options: { enabled?: boolean } = {},
): UseQueryResult<CustomerReport> {
  const client = useApiClient();
  return useQuery({
    queryKey: reportKeys.customers(params),
    queryFn: () => getCustomerReport(client, params),
    enabled: options.enabled ?? true,
  });
}

export function useFinancialReport(
  params: RangeParams & { basis: PnlBasis },
  options: { enabled?: boolean } = {},
): UseQueryResult<FinancialReport> {
  const client = useApiClient();
  return useQuery({
    queryKey: reportKeys.financial(params),
    queryFn: () => getFinancialReport(client, params),
    enabled: options.enabled ?? true,
  });
}
